import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@heidi/design-system/accordion';
import { Banner, BannerContent, BannerDescription } from '@heidi/design-system/banner';
import { TypographyP2, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { CircleHelp, Info, Mic } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { privacyStepMessages } from './privacy-step.messages';

/**
 * The five in-consultation reminders, in the order the approved copy specifies.
 * Hoisted so the array identity is stable across renders.
 */
const CONSULTATION_REMINDERS = [
  {
    title: privacyStepMessages.reminderConsentTitle,
    body: privacyStepMessages.reminderConsentBody,
  },
  {
    title: privacyStepMessages.reminderAmbiguousTitle,
    body: privacyStepMessages.reminderAmbiguousBody,
  },
  {
    title: privacyStepMessages.reminderNonVerbalTitle,
    body: privacyStepMessages.reminderNonVerbalBody,
  },
  {
    title: privacyStepMessages.reminderRetentionTitle,
    body: privacyStepMessages.reminderRetentionBody,
  },
  {
    title: privacyStepMessages.reminderReviewTitle,
    body: privacyStepMessages.reminderReviewBody,
  },
] as const;

const BLOCK_TITLE_CLASS = 'text-pretty text-text-primary';
const BLOCK_BODY_CLASS = 'text-pretty text-text-secondary';

/**
 * The pre-consult mic check, shown in both variants.
 *
 * `boxed` is the intended-use layout's bordered card. The not-a-medical-device
 * layout drops it: that statement is the only callout on the screen, and a
 * second bordered block next to it competes for the same attention.
 */
function MicTip({ boxed }: { boxed: boolean }) {
  const title = (
    <TypographyP2SemiBold className={BLOCK_TITLE_CLASS}>
      <FormattedMessage {...privacyStepMessages.micTipTitle} />
    </TypographyP2SemiBold>
  );
  const body = (
    <TypographyP2 className={BLOCK_BODY_CLASS}>
      <FormattedMessage {...privacyStepMessages.micTipBody} />
    </TypographyP2>
  );

  if (!boxed) {
    return (
      <div className="flex flex-col gap-1 border-t border-border pt-4">
        {title}
        {body}
      </div>
    );
  }

  return (
    <div className="flex gap-3 rounded-lg border border-border bg-surface p-3">
      <Mic className="mt-0.5 size-4 shrink-0 text-text-secondary" aria-hidden />
      <div className="flex flex-col gap-1">
        {title}
        {body}
      </div>
    </div>
  );
}

type PrivacyComplianceCopyProps =
  | {
      /**
       * The approved labelling for markets where Heidi is a regulated device:
       * a pointer to the Instructions for Use, the intended-use statements and
       * the in-consultation reminders.
       */
      variant: 'intended-use';
      /**
       * The intended-use statements, already translated. They predate
       * react-intl and still live in the i18next catalogue, so the caller
       * resolves them and passes them down.
       */
      intendedUse: {
        title: string;
        scribe: string;
        evidence: string | null;
      };
    }
  | {
      /**
       * The EU/EEA labelling: Heidi is not a medical device there, so the
       * intended-use statements above would themselves be a labelling act and
       * cannot appear on the same screen as this claim.
       */
      variant: 'not-a-medical-device';
    };

/**
 * Medical-device compliance copy for the final onboarding step (RCO-21 /
 * SWR-188). Which statement a market gets is the caller's decision — see
 * `isEuEeaCountryCode` in `PrivacyStep`.
 */
export function PrivacyComplianceCopy(props: PrivacyComplianceCopyProps) {
  if (props.variant === 'not-a-medical-device') {
    return (
      <>
        <MicTip boxed={false} />

        {/* No title: the banner already carries the emphasis, and a heading
            that repeated the closing sentence read as a second claim. Slany
            and Tim signed off the statement standing alone.

            Silent by design: this renders at mount rather than in response to
            an action, so it belongs in the reading order, not a live region. */}
        <Banner subVariant="default">
          <BannerContent icon={<Info />}>
            <BannerDescription>
              <FormattedMessage {...privacyStepMessages.notMedicalDeviceBody} />
            </BannerDescription>
          </BannerContent>
        </Banner>
      </>
    );
  }

  const { intendedUse } = props;

  return (
    <>
      <div className="flex flex-col gap-1 border-t border-border pt-4">
        <TypographyP2SemiBold className={BLOCK_TITLE_CLASS}>
          <FormattedMessage {...privacyStepMessages.ifuTitle} />
        </TypographyP2SemiBold>
        <TypographyP2 className={BLOCK_BODY_CLASS}>
          <FormattedMessage
            {...privacyStepMessages.ifuDescription}
            values={{
              helpIcon: (
                <CircleHelp className="inline size-4 align-text-bottom" aria-hidden />
              ),
            }}
          />
        </TypographyP2>
      </div>

      {/* `type="single"` so opening one panel collapses the other and the Get
          started CTA stays within reach. */}
      <Accordion type="single" collapsible className="divide-y divide-border">
        <AccordionItem value="intended-use" className="border-none">
          <AccordionTrigger className="py-2 text-start text-sm font-semibold text-text-primary hover:no-underline">
            {intendedUse.title}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3 pb-2">
            <TypographyP2 className={BLOCK_BODY_CLASS}>{intendedUse.scribe}</TypographyP2>
            {intendedUse.evidence ? (
              <TypographyP2 className={BLOCK_BODY_CLASS}>
                {intendedUse.evidence}
              </TypographyP2>
            ) : null}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="reminders" className="border-none">
          <AccordionTrigger className="py-2 text-start text-sm font-semibold text-text-primary hover:no-underline">
            <FormattedMessage {...privacyStepMessages.remindersTitle} />
          </AccordionTrigger>
          {/* The reminders run longer than the column, so they scroll inside
              the panel rather than pushing the CTA off-screen. */}
          <AccordionContent className="flex max-h-64 flex-col gap-4 overflow-y-auto pb-2 scrollbar-none">
            {CONSULTATION_REMINDERS.map(({ title, body }) => (
              <div key={title.id} className="flex flex-col gap-1">
                <TypographyP2SemiBold className={BLOCK_TITLE_CLASS}>
                  <FormattedMessage {...title} />
                </TypographyP2SemiBold>
                <TypographyP2 className={BLOCK_BODY_CLASS}>
                  <FormattedMessage {...body} />
                </TypographyP2>
              </div>
            ))}
            <TypographyP2 className={BLOCK_BODY_CLASS}>
              <FormattedMessage {...privacyStepMessages.remindersFooter} />
            </TypographyP2>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <MicTip boxed />
    </>
  );
}
